import React, { useState } from 'react'

import { createGenericContext } from '../../../../contexts/generic-context'
import type { TCarrierOnboardingStepID } from '../_types'

/**
 * Interface for Carrier Onboarding Context
 */
export interface ICarrierOnboardingContext {
  completedStepIDs: TCarrierOnboardingStepID[]
  isStepCompleted: (stepID: TCarrierOnboardingStepID) => boolean
  markStepAsCompleted: (stepID: TCarrierOnboardingStepID) => void
  resetSteps: () => void
}

/**
 * Create the carrier onboarding context using the generic context utility
 */
const [useCarrierOnboarding, CarrierOnboardingContextProvider] = createGenericContext<ICarrierOnboardingContext>(
  'useCarrierOnboarding must be used within CarrierOnboardingContextProvider'
)

/**
 * Provider component for the Carrier Onboarding context
 */
export const CarrierOnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [completedStepIDs, setCompletedStepIDs] = useState<TCarrierOnboardingStepID[]>([])

  const markStepAsCompleted = (stepID: TCarrierOnboardingStepID) => {
    setCompletedStepIDs((prev) => {
      if (prev.includes(stepID)) {
        return prev
      }
      return [...prev, stepID]
    })
  }

  const resetSteps = () => {
    setCompletedStepIDs([])
  }

  const isStepCompleted = (stepID: TCarrierOnboardingStepID) => {
    return completedStepIDs.includes(stepID)
  }

  const value: ICarrierOnboardingContext = {
    completedStepIDs,
    isStepCompleted,
    markStepAsCompleted,
    resetSteps
  }

  return <CarrierOnboardingContextProvider value={value}>{children}</CarrierOnboardingContextProvider>
}

export { useCarrierOnboarding }
